import { useState, useCallback, useEffectEvent, useEffect, useMemo } from 'react';
import {
  FileText,
  Sparkles,
  AlertCircle,
  Loader2,
  X,
  Plus,
  ChevronLeft,
  ChevronRight,
  Download,
  XCircle,
  Upload,
  CheckCircle2,
  FileCode,
} from 'lucide-react';
import { useDropzone, type FileRejection } from 'react-dropzone';
import { retry, sha256 } from '@/libs/utils';
import { getJDs, type GetJobDescriptionsResponseItem } from '@/apis/jobDescriptionsApis';
import { Button } from '../../components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../components/ui/tooltip';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  getDownloadCVUrl,
  getUploadCv,
  postUploadCV,
  putUploadBlob,
  type ListResumesCandidatesResponseItem,
  type ResumesCandidatesStatus,
  type UploadCVBody,
} from '../../apis/AICVScannerApis';

const PAGE_SIZE = 5;
const TIME_INTERVAL = 5000;

const SOURCE_OPTIONS = [
  'Apply',
  'Hunt',
  'Search',
  'Internal Referral',
  'Partner',
  'Other',
] as const;
const CHANNEL_OPTIONS = [
  'TopCV',
  'IT Việc',
  'Facebook',
  'LinkedIn',
  'Zalo',
  'Website',
  'Network',
  'Internal Referral',
  'Fanpage',
  'Headhunt',
  'VietCV',
  'Partner',
  'Other',
] as const;

type UploadStatus = 'READY' | 'PROCESSING' | 'SUCCESS' | 'FAILED';
type VisiblePageItem = number | 'ellipsis';

const processingStatusStyles: Record<ResumesCandidatesStatus, string> = {
  PENDING: 'bg-muted text-muted-foreground',
  DUPLICATED: 'bg-info-muted text-info-muted-foreground',
  SCORED: 'bg-success-muted text-success-muted-foreground',
  FAILED: 'bg-error-muted text-error-muted-foreground',
};

export const getProcessingStatusLabel = (status: ResumesCandidatesStatus) => {
  if (status === 'PENDING') {
    return 'Processing';
  }

  return status;
};

export const isProcessingStatus = (status: ResumesCandidatesStatus) => status === 'PENDING';

const SCORE_PASSED_CLASS = 'bg-success-muted text-success-muted-foreground';
const SCORE_FAILED_CLASS = 'bg-error-muted text-error-muted-foreground';

/**
 * Scored CVs show "Scored XX": green when the score reaches the JD threshold (and AI found no
 * blocking mismatch), red otherwise. Every scored CV goes to NEW either way; HR decides.
 */
export const getStatusBadge = (row: {
  ai_status: ResumesCandidatesStatus;
  ai_match_score?: number | null;
  ai_threshold?: number | null;
  ai_rejected?: boolean;
}) => {
  if (row.ai_status === 'SCORED' && typeof row.ai_match_score === 'number') {
    const passed =
      !row.ai_rejected &&
      (typeof row.ai_threshold !== 'number' || row.ai_match_score >= row.ai_threshold);

    return {
      label: `Scored ${Math.round(row.ai_match_score)}`,
      className: passed ? SCORE_PASSED_CLASS : SCORE_FAILED_CLASS,
    };
  }

  return {
    label: getProcessingStatusLabel(row.ai_status),
    className: processingStatusStyles[row.ai_status],
  };
};

/** Short, friendly text for the progress table; raw API text is for engineers. */
function userFacingProcessingNote(raw: string | null | undefined) {
  const r = raw?.trim();
  if (!r) {
    return 'We couldn’t read this CV. Try uploading it again, or use PDF or Word if you used another app.';
  }
  if (r.includes('not available in storage') || r.includes('5 seconds')) {
    return 'The file didn’t finish uploading in time. Check your connection and upload the CV again.';
  }
  if (r.includes('Unsupported file type for text extraction')) {
    return 'This file type isn’t supported. Please use PDF, Word (DOCX), or a plain .txt file.';
  }
  if (r.includes('did not contain any extractable text')) {
    return 'We couldn’t find any readable text. The file may be empty, a scan without text, or a protected document.';
  }
  if (r.startsWith('Extraction process failed:') || r.includes('Extraction process failed')) {
    return 'We couldn’t open or read this file. Try exporting a new PDF or DOCX, then upload again.';
  }
  return 'We couldn’t finish reading this CV. Please try a different file or contact support if this keeps happening.';
}

export function getUploadRejectionMessage(fileRejections: FileRejection[]) {
  for (const rejection of fileRejections) {
    if (rejection.errors.some((error) => error.code === 'file-too-large')) {
      return 'File size must not exceed 10MB.';
    }
  }

  for (const rejection of fileRejections) {
    if (rejection.errors.some((error) => error.code === 'file-too-small')) {
      return 'Uploaded file is empty. Please select a valid file.';
    }
  }

  for (const rejection of fileRejections) {
    if (rejection.errors.some((error) => error.code === 'file-invalid-type')) {
      return 'Unsupported file type. Please upload PDF, DOCX, TXT, or XLSX files.';
    }
  }

  for (const rejection of fileRejections) {
    if (rejection.errors.some((error) => error.code === 'too-many-files')) {
      return 'You can upload a maximum of 50 files.';
    }
  }

  return 'We could not upload one or more files. Please try again.';
}

export function getUserFacingUploadErrorMessage(error: unknown) {
  const rawMessage = error instanceof Error ? error.message : '';

  if (rawMessage.includes('File size must be a positive number')) {
    return 'Uploaded file is empty. Please select a valid file.';
  }

  return rawMessage || 'Failed to upload CVs to blob storage.';
}

export function getVisibleProcessingPages(
  currentPage: number,
  totalPages: number,
): VisiblePageItem[] {
  if (totalPages <= 0) {
    return [];
  }

  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const pages = new Set<number>([1, totalPages, currentPage - 1, currentPage, currentPage + 1]);
  const normalizedPages = [...pages]
    .filter((page) => page >= 1 && page <= totalPages)
    .sort((left, right) => left - right);

  const visibleItems: VisiblePageItem[] = [];

  for (const page of normalizedPages) {
    const lastItem = visibleItems[visibleItems.length - 1];
    if (typeof lastItem === 'number' && page - lastItem > 1) {
      visibleItems.push('ellipsis');
    }
    visibleItems.push(page);
  }

  return visibleItems;
}

export function formatFileSize(bytes: number) {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Keeps the start and the extension visible, e.g. "CV_Data_Engine...en_Van_C.docx". */
export function truncateMiddle(value: string, maxLength = 32) {
  if (value.length <= maxLength) {
    return value;
  }
  const keep = maxLength - 3;
  const head = Math.ceil(keep / 2);
  const tail = Math.floor(keep / 2);
  return `${value.slice(0, head)}...${value.slice(value.length - tail)}`;
}

function getFileExtension(fileName: string) {
  const dotIndex = fileName.lastIndexOf('.');
  return dotIndex >= 0 ? fileName.slice(dotIndex + 1).toUpperCase() : 'FILE';
}

export default function AICVScanner() {
  const [jds, setJds] = useState<GetJobDescriptionsResponseItem[]>([]);
  const [isLoadingJds, setIsLoadingJds] = useState<boolean>(false);
  const [files, setFiles] = useState<
    {
      uuid: string;
      file: File;
    }[]
  >([]);
  const [statusMap, setStatusMap] = useState<Record<string, UploadStatus>>({});
  const [selectedJdId, setSelectedJdId] = useState('');
  const [selectedSource, setSelectedSource] = useState('');
  const [selectedChannel, setSelectedChannel] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [processingPage, setProcessingPage] = useState(1);
  const [processingRefreshKey, setProcessingRefreshKey] = useState(0);
  const [processingCvs, setProcessingCvs] = useState<ListResumesCandidatesResponseItem[]>([]);
  const [isLoadingProcessingCvs, setIsLoadingProcessingCvs] = useState(false);
  const [totalProcessingCvs, setTotalProcessingCvs] = useState(0);
  const totalProcessingPages = useMemo(
    () => Math.ceil(totalProcessingCvs / 5),
    [totalProcessingCvs],
  );
  const visibleProcessingPages = useMemo(
    () => getVisibleProcessingPages(processingPage, totalProcessingPages),
    [processingPage, totalProcessingPages],
  );

  useEffect(() => {
    const fetchJDs = async () => {
      if (isLoadingJds) return;
      try {
        setIsLoadingJds(true);
        const response = await getJDs({
          page: 1,
          page_size: 100,
          status: 'ACTIVE',
        });
        setJds(response?.items || []);
      } catch (e) {
        console.error(e);
        // setJds([])
      } finally {
        setIsLoadingJds(false);
      }
    };
    void fetchJDs();
  }, []);

  useEffect(() => {
    const fetchProcessingCvs = async (withLoading = false) => {
      if (isLoadingProcessingCvs) return;
      try {
        if (withLoading) {
          setIsLoadingProcessingCvs(true);
        }
        const response = await getUploadCv({
          page: processingPage,
          page_size: PAGE_SIZE,
          mine_only: false,
        });
        setProcessingCvs(response?.items || []);
        setTotalProcessingCvs(response?.pagination?.total || 0);
      } catch (e) {
        console.error(e);
        // setProcessingCvs([])
        // setTotalProcessingCvs(0)
      } finally {
        if (withLoading) {
          setIsLoadingProcessingCvs(false);
        }
      }
    };
    const interval = setInterval(async () => {
      fetchProcessingCvs();
    }, TIME_INTERVAL);
    void fetchProcessingCvs(true);
    return () => {
      clearInterval(interval);
    };
  }, [processingPage, processingRefreshKey]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setError(null);
    const newFiles: {
      uuid: string;
      file: File;
    }[] = [];
    const newProgress: Record<string, number> = {};
    const newStatus: Record<string, UploadStatus> = {};
    for (const file of acceptedFiles) {
      const uuid = crypto.randomUUID();
      newFiles.push({
        uuid,
        file,
      });
      ((newProgress[uuid] = 0), (newStatus[uuid] = 'READY'));
    }

    setFiles((prev) => [...prev, ...newFiles]);
    setStatusMap((_prev) => ({
      ..._prev,
      ...newStatus,
    }));
  }, []);

  const onDropRejected = useCallback((fileRejections: FileRejection[]) => {
    setError(getUploadRejectionMessage(fileRejections));
  }, []);

  // The whole input panel is the drop target; the Upload CV / Add more buttons open the picker.
  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    onDropRejected,
    noClick: true,
    noKeyboard: true,
    accept: {
      'text/plain': ['.txt'],
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
    },
    multiple: true,
    disabled: isAnalyzing,
    minSize: 1,
    maxSize: 10 * 1024 * 1024,
    maxFiles: 50,
  } as any);

  const removeFileByUUID = useCallback((uuid: string) => {
    setFiles((_prev) => _prev.filter((file) => file.uuid !== uuid));
    setStatusMap((_prev) => {
      const newMap = { ..._prev };
      delete newMap[uuid];
      return newMap;
    });
  }, []);

  const readyFiles = useMemo(
    () => files.filter((file) => statusMap[file.uuid] === 'READY'),
    [files, statusMap],
  );
  const failedFileCount = useMemo(
    () => files.filter((file) => statusMap[file.uuid] === 'FAILED').length,
    [files, statusMap],
  );

  const handleUpload = useEffectEvent(async () => {
    if (readyFiles.length === 0 || !selectedJdId || !selectedSource || !selectedChannel) {
      setError('Please select a position, source, channel, and upload at least one CV.');
      return;
    }

    if (readyFiles.length > 50) {
      setError('You can upload a maximum of 50 files.');
      return;
    }

    const largeFiles = readyFiles.filter(
      (file) => file.file.size > 10 * 1024 * 1024, // > 10MB
    );

    if (largeFiles.length > 0) {
      const fileNames = largeFiles.map((f) => f.file.name).join(', ');
      setError(`These files exceed 10MB: ${fileNames}`);
      return;
    }

    const filesToUpload = [...readyFiles];

    if (filesToUpload.some((file) => file.file.size === 0)) {
      setError('Uploaded file is empty. Please select a valid file.');
      return;
    }

    const bodyFiles = await Promise.all(
      filesToUpload.map(async (file) => ({
        id: file.uuid,
        original_file_name: file.file.name,
        file_size: file.file.size,
        content_type: file.file.type,
        checksum_sha256: await sha256(file.file),
      })),
    );

    const body: UploadCVBody = {
      jd_id: selectedJdId,
      source: selectedSource,
      channel: selectedChannel,
      files: bodyFiles,
    };

    const uploadedIds = new Set<string>();

    try {
      setError(null);
      setIsAnalyzing(true);
      const response = await postUploadCV(body);

      const items = response?.items || [];
      if (items.length !== filesToUpload.length) {
        throw new Error('Upload initialization returned an unexpected number of files.');
      }

      const tasks = items.map((item) => {
        const rawFile = filesToUpload.find((file) => file.uuid === item.resume_id)?.file;
        if (!rawFile) {
          throw new Error(`Missing local file for upload item ${item.resume_id}.`);
        }
        return retry({
          callback: async () => {
            setStatusMap((_prev) => ({
              ..._prev,
              [item.resume_id]: 'PROCESSING',
            }));
            await putUploadBlob(item.upload.upload_url, rawFile);
            uploadedIds.add(item.resume_id);
            setStatusMap((_prev) => ({
              ..._prev,
              [item.resume_id]: 'SUCCESS',
            }));
          },
          onFailed: () =>
            setStatusMap((_prev) => ({
              ..._prev,
              [item.resume_id]: 'FAILED',
            })),
        });
      });

      await Promise.allSettled(tasks);
    } catch (err) {
      const message = getUserFacingUploadErrorMessage(err);
      setError(message);
      console.error(err);
    } finally {
      setIsAnalyzing(false);
    }

    if (uploadedIds.size > 0) {
      // Uploaded CVs now show up in the results table; only failed ones stay in the tray.
      setFiles((_prev) => _prev.filter((file) => !uploadedIds.has(file.uuid)));
      setStatusMap((_prev) => {
        const newMap = { ..._prev };
        for (const id of uploadedIds) {
          delete newMap[id];
        }
        return newMap;
      });
      setProcessingPage(1);
      setProcessingRefreshKey((key) => key + 1);
    }
  });

  const getIconStatus = useCallback((status: UploadStatus) => {
    switch (status) {
      case 'PROCESSING':
        return <Loader2 className="animate-spin text-primary shrink-0" size={14} />;

      case 'SUCCESS':
        return <CheckCircle2 className="text-success shrink-0" size={14} />;

      case 'FAILED':
        return <XCircle className="text-destructive shrink-0" size={14} />;

      default:
        return null;
    }
  }, []);

  const handleClearFiles = useCallback(() => {
    setFiles([]);
    setStatusMap({});
    setError(null);
  }, []);

  const SkeletonRow = useMemo(
    () => (
      <TableRow className="border-b border-border-subtle/50 animate-pulse">
        {/* CV Name */}
        <TableCell className="px-6 py-4 text-left">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-muted rounded shrink-0" />
            <div className="h-4 w-40 bg-muted rounded" />
          </div>
        </TableCell>

        {/* Position */}
        <TableCell className="px-6 py-4 text-left">
          <div className="h-4 w-32 bg-muted rounded mx-auto md:mx-0" />
        </TableCell>

        {/* Uploaded At */}
        <TableCell className="px-6 py-4 text-center">
          <div className="h-4 w-24 bg-muted rounded mx-auto" />
        </TableCell>

        {/* Status */}
        <TableCell className="px-6 py-4 text-center">
          <div className="h-6 w-20 bg-muted rounded-full mx-auto" />
        </TableCell>

        {/* Note */}
        <TableCell className="px-6 py-4 text-center">
          <div className="h-5 w-5 bg-muted rounded mx-auto" />
        </TableCell>

        {/* Actions */}
        <TableCell className="px-6 py-4 text-center">
          <div className="flex items-center justify-center gap-2">
            <div className="w-6 h-6 bg-muted rounded-md" />
            <div className="w-6 h-6 bg-muted rounded-md" />
          </div>
        </TableCell>
      </TableRow>
    ),
    [],
  );

  const handleClickDownloadCv = async (resumeId: string) => {
    try {
      const response = await getDownloadCVUrl(resumeId);
      window.open(response.download_url, '_blank');
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div className="space-y-6 pb-12">
      <h1 className="text-3xl font-bold text-text-main">CV Screener</h1>

      <section
        {...getRootProps()}
        className={`bg-card p-5 rounded-xl border shadow-sm space-y-4 transition-colors ${
          isDragActive ? 'border-primary bg-primary-light' : 'border-border-subtle'
        }`}
      >
        <input {...getInputProps()} />

        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-60 flex-[2]">
            <label className="block text-sm font-semibold text-text-main mb-2">Job Position</label>
            <Select
              value={selectedJdId}
              onValueChange={setSelectedJdId}
              disabled={isAnalyzing || isLoadingJds}
            >
              <SelectTrigger className="w-full bg-bg-main">
                <SelectValue placeholder="Choose a position..." />
              </SelectTrigger>
              <SelectContent>
                {jds.map((jd) => (
                  <SelectItem key={jd.id} value={jd.id}>
                    {jd.title} {jd.department ? `(${jd.department})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="min-w-44 flex-1">
            <label className="block text-sm font-semibold text-text-main mb-2">Source</label>
            <Select value={selectedSource} onValueChange={setSelectedSource} disabled={isAnalyzing}>
              <SelectTrigger className="w-full bg-bg-main">
                <SelectValue placeholder="Choose a source..." />
              </SelectTrigger>
              <SelectContent>
                {SOURCE_OPTIONS.map((source) => (
                  <SelectItem key={source} value={source}>
                    {source}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="min-w-44 flex-1">
            <label className="block text-sm font-semibold text-text-main mb-2">Channel</label>
            <Select
              value={selectedChannel}
              onValueChange={setSelectedChannel}
              disabled={isAnalyzing}
            >
              <SelectTrigger className="w-full bg-bg-main">
                <SelectValue placeholder="Choose a channel..." />
              </SelectTrigger>
              <SelectContent>
                {CHANNEL_OPTIONS.map((channel) => (
                  <SelectItem key={channel} value={channel}>
                    {channel}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Button
            type="button"
            variant="outline"
            onClick={open}
            disabled={isAnalyzing}
            className="min-w-44 flex-1 border-dashed font-semibold"
          >
            <Upload size={16} />
            <span>Upload CV</span>
            {files.length > 0 ? (
              <Badge variant="secondary" className="rounded-full px-2 text-xs">
                {files.length}
              </Badge>
            ) : null}
          </Button>

          <Button
            type="button"
            onClick={handleUpload}
            disabled={
              readyFiles.length === 0 ||
              !selectedJdId ||
              !selectedSource ||
              !selectedChannel ||
              isAnalyzing
            }
            className="min-w-36 font-semibold"
          >
            {isAnalyzing ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
            <span>
              {isAnalyzing
                ? 'Analyzing...'
                : readyFiles.length > 0
                  ? `Analyze ${readyFiles.length} CV${readyFiles.length > 1 ? 's' : ''}`
                  : 'Analyze CV'}
            </span>
          </Button>
        </div>

        {files.length > 0 && (
          <div className="rounded-lg border border-border-subtle bg-bg-main/40 p-3">
            <div className="mb-3 flex items-center justify-between gap-2">
              <span className="text-sm font-semibold text-text-main">
                {readyFiles.length} CV{readyFiles.length === 1 ? '' : 's'} ready
                {failedFileCount > 0 ? (
                  <span className="text-destructive"> · {failedFileCount} failed</span>
                ) : null}
              </span>
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={open}
                  disabled={isAnalyzing}
                  className="text-primary hover:text-primary"
                >
                  <Plus size={14} /> Add more
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleClearFiles}
                  disabled={isAnalyzing}
                  className="text-text-subtle"
                >
                  Clear all
                </Button>
              </div>
            </div>
            <div className="flex max-h-48 flex-wrap gap-2 overflow-y-auto">
              {files.map((file) => {
                const status = statusMap[file.uuid] || 'FAILED';
                return (
                  <div
                    key={file.uuid}
                    title={file.file.name}
                    className={`flex w-70 items-center gap-2 rounded-md border bg-card px-3 py-2 ${
                      status === 'FAILED' ? 'border-destructive/50' : 'border-border-subtle'
                    }`}
                  >
                    <span className="shrink-0 rounded bg-primary/15 px-1.5 py-0.5 text-[10px] font-bold text-primary">
                      {getFileExtension(file.file.name)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-text-main">
                        {truncateMiddle(file.file.name)}
                      </p>
                      <p className="text-xs text-text-subtle">
                        {status === 'FAILED' ? 'Upload failed' : formatFileSize(file.file.size)}
                      </p>
                    </div>
                    {getIconStatus(status)}
                    {(status === 'READY' || status === 'FAILED') && (
                      <Button
                        type="button"
                        onClick={() => {
                          removeFileByUUID(file.uuid);
                        }}
                        disabled={isAnalyzing}
                        variant="ghost"
                        size="icon-sm"
                        className="size-6 rounded-full text-text-subtle"
                        aria-label={`Remove ${file.file.name}`}
                      >
                        <X size={12} />
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {error && (
          <div className="flex items-center gap-3 text-error-muted-foreground bg-error-muted p-3 rounded-lg text-sm border border-error/30">
            <AlertCircle size={16} className="shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <p className="text-xs text-text-subtle">
          Drop one or more CVs anywhere on this panel · PDF, DOCX, TXT, XLSX · max 10MB each
        </p>
      </section>

      <section className="bg-card rounded-xl border border-border-subtle shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-border-subtle flex items-center justify-between">
          <h2 className="text-xl font-bold text-text-main">Screening Results</h2>
          <span className="text-xs font-bold uppercase tracking-widest text-text-subtle">
            {totalProcessingCvs} files
          </span>
        </div>

        <div className="overflow-x-auto">
          <Table className="min-w-200">
            <TableHeader>
              <TableRow className="bg-bg-main/40 border-border-subtle/60 text-xs uppercase tracking-widest text-text-subtle">
                <TableHead className="px-6 py-3 font-bold">CV name</TableHead>
                <TableHead className="px-6 py-3 font-bold">Position</TableHead>
                <TableHead className="px-6 py-3 font-bold">Upload date</TableHead>
                <TableHead className="px-6 py-3 font-bold">Status</TableHead>
                <TableHead className="px-6 py-3 font-bold w-20 text-center">Note</TableHead>
                <TableHead className="px-6 py-3 font-bold">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoadingProcessingCvs ? (
                <>
                  {SkeletonRow}
                  {SkeletonRow}
                  {SkeletonRow}
                  {SkeletonRow}
                  {SkeletonRow}
                </>
              ) : (
                processingCvs.map((row) => {
                  const statusBadge = getStatusBadge(row);

                  return (
                    <TableRow
                      key={row.resume_id}
                      className="border-b border-border-subtle/50 last:border-0 hover:bg-bg-main/20 transition-colors"
                    >
                      <TableCell className="px-6 py-4 text-left">
                        <div className="flex items-center gap-2">
                          <FileText size={14} className="text-primary shrink-0" />
                          <span className="text-sm font-medium text-text-main">{row.cv_name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="px-6 py-4 text-left text-sm text-text-main">
                        {row.position}
                      </TableCell>
                      <TableCell className="px-6 py-4 text-center text-sm text-text-subtle">
                        {new Date(row.uploaded_at).toLocaleDateString('en-GB')}
                      </TableCell>
                      <TableCell className="px-6 py-4 text-center">
                        <div className="flex flex-col items-center gap-1.5">
                          <Badge
                            variant="secondary"
                            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold uppercase tracking-wide ${statusBadge.className}`}
                          >
                            {isProcessingStatus(row.ai_status) ? (
                              <Loader2 size={12} className="animate-spin" aria-hidden="true" />
                            ) : null}
                            {statusBadge.label}
                          </Badge>
                          {isProcessingStatus(row.ai_status) ? (
                            <div className="w-28 space-y-1" aria-label="AI processing indicator">
                              <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                                <div className="h-full w-1/2 animate-pulse rounded-full bg-primary" />
                              </div>
                              <div className="text-[11px] font-medium text-text-subtle">
                                AI is calculating the score...
                              </div>
                            </div>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell className="px-6 py-4 text-center">
                        {row.extraction_status === 'FAILED' ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                className="inline-flex items-center justify-center p-1 rounded-md text-warning-muted-foreground hover:bg-warning-muted transition-colors cursor-help"
                                aria-label="View a short note about this upload"
                              >
                                <FileCode size={18} strokeWidth={1.75} />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent
                              side="left"
                              className="max-w-xs text-left text-xs text-balance leading-relaxed"
                            >
                              {userFacingProcessingNote(row.extraction_failure_reason)}
                            </TooltipContent>
                          </Tooltip>
                        ) : null}
                      </TableCell>
                      <TableCell className="px-6 py-4 text-center">
                        <Button
                          onClick={() => {
                            handleClickDownloadCv(row.resume_id);
                          }}
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          className="p-1.5 rounded-md hover:bg-bg-main text-text-subtle hover:text-primary transition-colors"
                          title="Download CV"
                        >
                          <Download size={14} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        <div className="px-6 py-4 border-t border-border-subtle bg-bg-main/20 flex items-center justify-between">
          <div className="text-sm text-text-subtle">
            {totalProcessingCvs > 0 && (
              <>
                Showing{' '}
                <span className="font-semibold text-text-main">
                  {(processingPage - 1) * PAGE_SIZE + 1}
                </span>{' '}
                to{' '}
                <span className="font-semibold text-text-main">
                  {Math.min(processingPage * PAGE_SIZE, totalProcessingCvs)}
                </span>{' '}
                of <span className="font-semibold text-text-main">{totalProcessingCvs}</span>
              </>
            )}
          </div>

          <div className="flex items-center gap-1">
            <Button
              type="button"
              onClick={() => setProcessingPage((page) => Math.max(1, page - 1))}
              disabled={processingPage === 1}
              variant="outline"
              size="icon-sm"
              className="p-1.5 rounded-md border border-border-subtle bg-card text-text-subtle hover:text-primary hover:border-primary disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft size={14} />
            </Button>
            {visibleProcessingPages.map((item, index) =>
              item === 'ellipsis' ? (
                <span
                  key={`ellipsis-${index}`}
                  className="flex h-8 min-w-8 items-center justify-center text-sm font-semibold text-text-subtle"
                >
                  ...
                </span>
              ) : (
                <Button
                  key={item}
                  type="button"
                  onClick={() => setProcessingPage(item)}
                  variant={item === processingPage ? 'primary' : 'outline'}
                  size="sm"
                  className={`min-w-8 h-8 rounded-md border text-sm font-semibold transition-colors ${
                    item === processingPage
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border-subtle bg-card text-text-subtle hover:border-primary hover:text-primary'
                  }`}
                >
                  {item}
                </Button>
              ),
            )}
            <Button
              type="button"
              onClick={() => setProcessingPage((page) => Math.min(totalProcessingPages, page + 1))}
              disabled={processingPage >= totalProcessingPages}
              variant="outline"
              size="icon-sm"
              className="p-1.5 rounded-md border border-border-subtle bg-card text-text-subtle hover:text-primary hover:border-primary disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight size={14} />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
